// src/components/booking/BookingCalendar.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Loads booked slots for the current view-month from Firestore in real time.
// A slot document lives at:  bookings/{YYYY-MM-DD_HH:mm}
// with fields: { date: "YYYY-MM-DD", time: "HH:mm", status: "confirmed" | ... }
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useMemo, useState } from "react";
import { collection, query, where, getDocs, doc, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";
import { filterAppointmentTimes, isTimeBlocked } from "./bookingAvailability";

const DEFAULT_WEEKDAY_TIMES = [
	"09:00", "10:00", "11:00", "12:00", "13:00",
	"14:00", "15:00", "16:00", "17:00", "18:00",
];
const DEFAULT_SATURDAY_TIMES = ["09:00", "10:00", "11:00", "12:00"];

const MONTH_NAMES = [
	"January", "February", "March", "April", "May", "June",
	"July", "August", "September", "October", "November", "December",
];
const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function toISODate(date) {
	const y = date.getFullYear();
	const m = String(date.getMonth() + 1).padStart(2, "0");
	const d = String(date.getDate()).padStart(2, "0");
	return `${y}-${m}-${d}`;
}

function isPastDay(date, today) {
	return date < today;
}

function isWeekend(date) {
	const day = date.getDay();
	return day === 0 || day === 6;
}

function isWeekendOpen(date, availability) {
	return date.getDay() === 6 ? availability.saturday : availability.sunday;
}

function timesForDate(date, availability, service) {
	const times = date.getDay() === 6
		? availability.saturdayTimes
		: date.getDay() === 0 ? availability.sundayTimes : availability.weekdayTimes;
	const validTimes = (Array.isArray(times) ? times : []).filter((time) => /^([01]\d|2[0-3]):[0-5]\d$/.test(time));
	return filterAppointmentTimes(validTimes, service.mode);
}

// Returns "YYYY-MM" string for a given year + month index.
function toYearMonth(year, month) {
	return `${year}-${String(month + 1).padStart(2, "0")}`;
}

const BookingCalendar = ({
	service,
	onSelect,
	onBack,
	stepLabel = "Step 2 of 3",
	heading = "Pick a date & time",
	description,
}) => {
	const today = useMemo(() => {
		const d = new Date();
		d.setHours(0, 0, 0, 0);
		return d;
	}, []);

	const [viewDate, setViewDate] = useState(
		new Date(today.getFullYear(), today.getMonth(), 1)
	);
	const [selectedDate, setSelectedDate] = useState(null);
	const [selectedTime, setSelectedTime] = useState(null);

	// bookedSlots: { "YYYY-MM-DD": ["09:00", "11:00", ...], ... }
	const [bookedSlots, setBookedSlots] = useState({});
	const [loadingSlots, setLoadingSlots] = useState(false);
	const [weekendAvailability, setWeekendAvailability] = useState({
		saturday: false,
		sunday: false,
		weekdayTimes: DEFAULT_WEEKDAY_TIMES,
		saturdayTimes: DEFAULT_SATURDAY_TIMES,
		sundayTimes: DEFAULT_SATURDAY_TIMES,
	});
	const [blockedDates, setBlockedDates] = useState([]);
	const [blockedSlots, setBlockedSlots] = useState([]);
	const [blockedTimeRanges, setBlockedTimeRanges] = useState([]);

	const year  = viewDate.getFullYear();
	const month = viewDate.getMonth();

	// ── Fetch booked slots from Firestore whenever the month view changes ──────
	useEffect(() => {
		let cancelled = false;
		setLoadingSlots(true);
		setBookedSlots({});

		const yearMonth = toYearMonth(year, month);

		// Query all confirmed bookings in this calendar month.
		// Firestore rule: `date` field starts with "YYYY-MM"
		const q = query(
			collection(db, "bookings"),
			where("yearMonth", "==", yearMonth),
			where("status", "in", ["confirmed", "pending"])
		);

		getDocs(q)
			.then((snapshot) => {
				if (cancelled) return;
				const slots = {};
				snapshot.forEach((doc) => {
					const { date, time } = doc.data();
					if (!slots[date]) slots[date] = [];
					slots[date].push(time);
				});
				setBookedSlots(slots);
			})
			.catch((err) => {
				if (!cancelled) console.error("Failed to load availability:", err);
			})
			.finally(() => {
				if (!cancelled) setLoadingSlots(false);
			});

		return () => { cancelled = true; };
	}, [year, month]);

	useEffect(() => {
		const unsubscribe = onSnapshot(
			doc(db, "settings", "bookingAvailability"),
			(snapshot) => {
				const data = snapshot.exists() ? snapshot.data() : {};
				const saturdayTimes = Array.isArray(data.saturdayTimes) ? data.saturdayTimes : DEFAULT_SATURDAY_TIMES;
				setWeekendAvailability({
					saturday: data.saturday === true,
					sunday: data.sunday === true,
					weekdayTimes: Array.isArray(data.weekdayTimes) ? data.weekdayTimes : DEFAULT_WEEKDAY_TIMES,
					saturdayTimes,
					sundayTimes: Array.isArray(data.sundayTimes) ? data.sundayTimes : saturdayTimes,
				});
				setBlockedDates(Array.isArray(data.blockedDates) ? data.blockedDates : []);
				setBlockedSlots(Array.isArray(data.blockedSlots) ? data.blockedSlots : []);
				setBlockedTimeRanges(Array.isArray(data.blockedTimeRanges) ? data.blockedTimeRanges : []);
			},
			() => {
				setWeekendAvailability({
					saturday: false,
					sunday: false,
					weekdayTimes: DEFAULT_WEEKDAY_TIMES,
					saturdayTimes: DEFAULT_SATURDAY_TIMES,
					sundayTimes: DEFAULT_SATURDAY_TIMES,
				});
				setBlockedDates([]);
				setBlockedSlots([]);
				setBlockedTimeRanges([]);
			}
		);

		return unsubscribe;
	}, []);

	// ── Calendar grid ──────────────────────────────────────────────────────────
	const calendarCells = useMemo(() => {
		const firstOfMonth = new Date(year, month, 1);
		const startOffset  = firstOfMonth.getDay();
		const daysInMonth  = new Date(year, month + 1, 0).getDate();

		const cells = [];
		for (let i = 0; i < startOffset; i++) cells.push(null);
		for (let day = 1; day <= daysInMonth; day++) {
			cells.push(new Date(year, month, day));
		}
		return cells;
	}, [year, month]);

	const isCurrentOrEarlierMonth =
		year < today.getFullYear() ||
		(year === today.getFullYear() && month <= today.getMonth());

	const goToPrevMonth = () => {
		if (isCurrentOrEarlierMonth) return;
		setViewDate(new Date(year, month - 1, 1));
		setSelectedDate(null);
		setSelectedTime(null);
	};
	const goToNextMonth = () => {
		setViewDate(new Date(year, month + 1, 1));
		setSelectedDate(null);
		setSelectedTime(null);
	};

	const handleDayClick = (date) => {
		if (!date) return;
		const iso   = toISODate(date);
		const workingHours = timesForDate(date, weekendAvailability, service);
		if (isPastDay(date, today) || blockedDates.includes(iso) || (isWeekend(date) && !isWeekendOpen(date, weekendAvailability))) return;
		const taken = bookedSlots[iso] || [];
		if (workingHours.length === 0 || workingHours.every((time) => (
			taken.includes(time) || isTimeBlocked(iso, time, blockedSlots, blockedTimeRanges)
		))) return;
		setSelectedDate(iso);
		setSelectedTime(null);
	};

	const takenTimesForSelected = selectedDate ? bookedSlots[selectedDate] || [] : [];

	const handleContinue = () => {
		if (!selectedDate || !selectedTime) return;
		onSelect(selectedDate, selectedTime);
	};

	return (
		<div className="booking-calendar">
			<div className="booking-step-heading">
				<div className="step-eyebrow">{stepLabel}</div>
				<h2 className="step-title">{heading}</h2>
				<p className="step-subtitle">
					{description || <>Booking <strong>{service.title}</strong> &middot; {service.duration} min session</>}
				</p>
			</div>

			<div className="calendar-card">
				<div className="calendar-header">
					<button
						type="button"
						className="calendar-nav"
						onClick={goToPrevMonth}
						disabled={isCurrentOrEarlierMonth}
						aria-label="Previous month"
					>
						&larr;
					</button>
					<div className="calendar-month-label">
						{MONTH_NAMES[month]} {year}
						{loadingSlots && (
							<span className="calendar-loading-indicator"> ·</span>
						)}
					</div>
					<button
						type="button"
						className="calendar-nav"
						onClick={goToNextMonth}
						aria-label="Next month"
					>
						&rarr;
					</button>
				</div>

				<div className="calendar-day-labels">
					{DAY_LABELS.map((label) => (
						<div key={label} className="calendar-day-label">{label}</div>
					))}
				</div>

				<div className="calendar-grid">
					{calendarCells.map((date, index) => {
						if (!date) {
							return (
								<div
									key={`empty-${index}`}
									className="calendar-cell calendar-cell-empty"
								/>
							);
						}

						const iso         = toISODate(date);
						const taken       = bookedSlots[iso] || [];
						const workingHours = timesForDate(date, weekendAvailability, service);
						const fullyBooked = workingHours.length > 0 && workingHours.every((time) => (
							taken.includes(time) || isTimeBlocked(iso, time, blockedSlots, blockedTimeRanges)
						));
						const unavailable = isPastDay(date, today) || blockedDates.includes(iso) || (isWeekend(date) && !isWeekendOpen(date, weekendAvailability));
						const disabled    = unavailable || fullyBooked;
						const isSelected  = selectedDate === iso;

						return (
							<button
								key={iso}
								type="button"
								className={[
									"calendar-cell",
									disabled  ? "calendar-cell-disabled"  : "calendar-cell-available",
									isSelected ? "calendar-cell-selected" : "",
								].join(" ").trim()}
								onClick={() => handleDayClick(date)}
								disabled={disabled}
								aria-pressed={isSelected}
							>
								<span className="calendar-cell-number">{date.getDate()}</span>
								{blockedDates.includes(iso) && !isPastDay(date, today) && (
									<span className="calendar-cell-tag">Unavailable</span>
								)}
								{fullyBooked && !unavailable && (
									<span className="calendar-cell-tag">Full</span>
								)}
							</button>
						);
					})}
				</div>
			</div>

			{selectedDate && (
				<div className="time-slot-panel">
					<div className="time-slot-heading">
						Available times for{" "}
						{new Date(`${selectedDate}T00:00:00`).toLocaleDateString("en-ZA", {
							weekday: "long",
							day: "numeric",
							month: "long",
						})}
					</div>
					<div className="time-slot-grid">
						{timesForDate(new Date(`${selectedDate}T00:00:00`), weekendAvailability, service).map((time) => {
							const isBooked   = takenTimesForSelected.includes(time)
								|| isTimeBlocked(selectedDate, time, blockedSlots, blockedTimeRanges);
							const isSelected = selectedTime === time;
							return (
								<button
									key={time}
									type="button"
									className={[
										"time-slot",
										isBooked   ? "time-slot-booked"   : "time-slot-open",
										isSelected ? "time-slot-selected" : "",
									].join(" ").trim()}
									disabled={isBooked}
									onClick={() => setSelectedTime(time)}
								>
									{time}
								</button>
							);
						})}
					</div>
				</div>
			)}

			<div className="step-actions">
				<button type="button" className="btn btn-ghost" onClick={onBack}>
					&larr; Back
				</button>
				<button
					type="button"
					className="btn btn-primary"
					onClick={handleContinue}
					disabled={!selectedDate || !selectedTime}
				>
					Continue
				</button>
			</div>
		</div>
	);
};

export default BookingCalendar;